import React from 'react';

import { DetailedFormType, GP_SLOT_VALUES } from '../constants/DetailedBookingConstants';
import { FORM_FIELDS } from '../constants/FormFieldsConstants';
import { BookingFormData, ContextualNode } from './BookingFormInterface';
import { Option } from './FormFieldInterfaces';


export interface PreviousNodesValues {
  valueToBeLockedAtPackageLevel: number;
  impressions: number;
  approxShareBlockedOverNode: number;
};

export interface DetailedBookingIMPPartialDataFields extends PreviousNodesValues {
  entryNumber: number;
  slotForGP: string;
  valueBlockedByLineItem: number;
  impressionsBlockedOverContextualNode: number;
  imSegment: string;
  bookingId: string
}

interface CommonDetailedBookingIMPMainFormFields {
  slotForGP: string;
  valueBlockedByLineItem: string;
  impressionsBlockedOverContextualNode: string;
}
export interface DetailedBookingIMPMainFormFields extends CommonDetailedBookingIMPMainFormFields {
  entryNumber: number;
}
export interface DetailedBookingIMPMainFormFieldErrors extends CommonDetailedBookingIMPMainFormFields {
  entryNumber: string;
}

export interface DetailedBookingIMPData
  extends DetailedBookingIMPPartialDataFields {
  brand: string;
  aeName: string;
  category: string;
  typeOfPurchase: string;
}

export interface ContextualNodeFields {
  sovPercentageForContextualGP: number;
  impressionBooked: number;
  valueBooked: number;
}

export interface DetailedBookingSOVPartialData extends ContextualNodeFields {
  entryNumber: number;
  bookingId: string;
  gpSlot: string;
  sovPercentageForGP: number;
  valueBlockedByLineItem: number;
  contextualNode: string;
  impressionsForNonContextual?: number;
}

export interface DetailedBookingSOVPartialDataErrors {
  entryNumber: string;
  gpSlot: string;
  sovPercentageForGP: string;
  valueBlockedByLineItem: string;
  contextualNode: string;
  sovPercentageForContextualGP: string;
  impressionBooked: string;
  valueBooked: string;
}

export interface DetailedBookingSOVData extends DetailedBookingSOVPartialData {
  brand: string;
  aeName: string;
  category: string;
  typeOfPurchase: string;
}

interface CommonDetailedBookingFormFields {
  initialData: BookingFormData;
  entryId: number;
  isEditMode: boolean;
  bookingId?: string | null;
  validateFormTrigger?: boolean;
  existingContextualNodes?: ContextualNode[] | null;
  resetDetailedFormErrors?: boolean;
  setResetDetailedFormErrors?: (value: boolean) => void;
  filteredSlotPackages?: SlotPackage[] | null;
  hideCommonFields: boolean;
  onSlotSelectionChange: (slotName: string) => void;
  preSelectedSlotNames?: string[];
}

export interface CreateDetailedBookingIMPFormProps extends CommonDetailedBookingFormFields {
  onFormSubmit?: (
    formData: DetailedBookingIMPData,
    contextualNodes: IMPContextualNodeData[]
  ) => void;
  budgetUsedByOtherSlots?: number;
  onSlotBudgetChange?: (totalBudget: number) => void;
}

export interface CreateDetailedBookingSOVFormProps extends CommonDetailedBookingFormFields {
  onFormSubmit?: (
    formData: DetailedBookingSOVData,
    contextualNodes: SOVContextualNodeData[]
  ) => void;
  budgetUsedByOtherSlots?: number;
  onSlotBudgetChange?: (totalBudget: number) => void;
}

export interface IMPContextualNodeData extends PreviousNodesValues {
  id: string;
  imSegment: string;
}

export interface SOVContextualNodeData extends ContextualNodeFields {
  id: string;
  contextualNode: string;
}

export interface IMPContextualNodeErrors {
  imSegment: string;
  valueToBeLockedAtPackageLevel: string;
  impressions: string;
  approxShareBlockedOverNode: string;
}

export interface SOVContextualNodeErrors {
  contextualNode: string;
  sovPercentageForContextualGP: string;
  impressionBooked: string;
  valueBooked: string;
}

export interface CreateBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  editBookingId?: string | null;
}

export enum BookingMode {
  CREATE = 'create',
  EDIT = 'edit'
}

export interface SlotOptionType extends Option {
  type: GP_SLOT_VALUES;
}

export type SovActiveModalType = React.Dispatch<React.SetStateAction<
  typeof FORM_FIELDS.VALUE_BLOCKED_BY_LINE_ITEM
  | typeof FORM_FIELDS.SOV_PERCENTAGE_FOR_GP
  | typeof FORM_FIELDS.SOV_PERCENTAGE_FOR_CONTEXTUAL_GP
  | typeof FORM_FIELDS.VALUE_BOOKED | null>>

interface SlotPackageSegment {
  segmentName: string;
  category: string;
  overAllImpressions: number;
  impressionsBooked: number;
  cpm: number;
  overAllValue: number;
  availability: number;
  percentageAvailable: number;
}

export interface SlotPackage {
  slotPackageName: string;
  segments: SlotPackageSegment[];
}

export interface SlotPackagesResponse {
  statusCode: number;
  message: string;
  slotPackages: SlotPackage[];
}

export interface PreviousSOVValues {
  sovPercentageForGP: number;
  valueBlockedByLineItem: number;
  impressionsForNonContextual?: number;
  impressionBooked?: number;
  contextualNode?: ContextualNodeFields;
}

export interface SlotEntryData {
  formData: DetailedBookingIMPData | DetailedBookingSOVData;
  contextualNodes: IMPContextualNodeData[] | SOVContextualNodeData[];
  detailedFormType: DetailedFormType;
}

export interface SlotEntry {
  id: string;
  collectedData: SlotEntryData | null;
}

export interface MultiSlotDetailedFormProps {
  initialData: BookingFormData;
  entryId: number;
  isEditMode: boolean;
  bookingId?: string | null;
  detailedFormType: DetailedFormType;
  existingContextualNodes?: ContextualNode[] | null;
  resetDetailedFormErrors?: boolean;
  setResetDetailedFormErrors?: (value: boolean) => void;
  filteredSlotPackages?: SlotPackage[] | null;
  validateFormTrigger: boolean;
  onAllSlotsSubmit: (allSlotEntries: SlotEntryData[]) => void;
}